import { HttpService, Query, QueryHandler, TaggedUrl, Tags } from '@skylab-shared/core';
import { InventoryDocumentKind, InventoryEffect, KsefDocumentStatus, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { fillPlaceholders } from '@skylab-shared/utils';

export interface DocumentLinks {
    readonly advances: DocumentLink[];
    readonly current: DocumentLink;
    readonly adjustments?: DocumentLink[] | null;
    readonly implementing?: DocumentLink[] | null;
    readonly implemented?: DocumentLink[] | null;
    readonly commercial?: DocumentLink[] | null;
    readonly inventory?: DocumentLink[] | null;
}

export interface DocumentLink {
    readonly role: DocumentLinkKind;
    readonly documentId: number;
    readonly documentNumber: string;
    readonly kind: SaleDocumentKind | InventoryDocumentKind | PurchaseDocumentKind;
    readonly version: string;
    readonly adjustedDocumentId?: number;
    readonly ksefDocumentStatus: KsefDocumentStatus;
    readonly ksefXmlImported: boolean;
    readonly relatedCommercialDocumentId?: number;
    readonly relatedInventoryDocumentId?: number;
    readonly issueGoodsReceiptAdjustment: boolean;
    readonly inventoryEffect: InventoryEffect;
}

export enum DocumentLinkKind {
    Original = 'Original',
    Adjustment = 'Adjustment',
    Adjusted = 'Adjusted',
    Advance = 'Advance',
    ImplementingDocument = 'ImplementingDocument',
    ImplementedDocument = 'ImplementedDocument',
    Commercial = 'Commercial',
    Inventory = 'Inventory',
}

export class DocumentLinksQuery extends Query<DocumentLinks> {
    public constructor(public readonly documentId: number) {
        super();
    }
}

export class DocumentLinksQueryHandlerBase implements QueryHandler<DocumentLinksQuery> {
    private static Url = TaggedUrl.join('documents/:id/links', [Tags.Api]);

    public constructor(private readonly http: HttpService) { }

    public async handleAsync(query: DocumentLinksQuery): Promise<DocumentLinks> {
        const url = fillPlaceholders(DocumentLinksQueryHandlerBase.Url, { id: query.documentId });
        const { data } = await this.http.get<DocumentLinks>(url);

        return {
            current: this.mapLink(data.current, DocumentLinkKind.Original),
            advances: data.advances?.map(item => this.mapLink(item, DocumentLinkKind.Advance)) || null,
            adjustments: data.adjustments?.map((item, index) => this.mapLink(item, index === 0 ? DocumentLinkKind.Original : DocumentLinkKind.Adjustment)) || null,
            implementing: data.implementing?.map(item => this.mapLink(item, DocumentLinkKind.ImplementingDocument)) || null,
            implemented: data.implemented?.map(item => this.mapLink(item, DocumentLinkKind.ImplementedDocument)) || null,
            commercial: data.commercial?.map(item => this.mapLink(item, DocumentLinkKind.Commercial)) || null,
            inventory: data.inventory?.map(item => this.mapLink(item, DocumentLinkKind.Inventory)) || null
        };
    }

    private mapLink(data: any, role: DocumentLinkKind): DocumentLink {
        return {
            role: role,
            documentId: data.id,
            documentNumber: data.documentNumber,
            kind: data.kind,
            version: data.version,
            adjustedDocumentId: data.adjustedDocumentId,
            ksefDocumentStatus: data.ksefDocumentStatus,
            ksefXmlImported: data.ksefXmlImported,
            relatedCommercialDocumentId: data.relatedCommercialDocumentId,
            relatedInventoryDocumentId: data.relatedInventoryDocumentId,
            issueGoodsReceiptAdjustment: data.issueGoodsReceiptAdjustment,
            inventoryEffect: data.inventoryEffect
        };
    }
}
